import * as graphsApi from "@/src/api/graphs";
import {OP, trackPending} from "@/src/queries/pending";
import * as studioApi from "@/src/api/studio";
import {xmlToJson} from "@/lib/import/Util";
import {getActivityList, getActivityName} from "@/lib/util/ModelUtil";
import {JobStatus} from "@/src/model/JobStatus";

// keep in sync with GraphLockService.LOCK_TIMEOUT on the backend, which applies the same
// expiry to every lock check
const LOCK_MINUTES = 10;

export default {
    namespaced: true,
    state: {
        jsonData: null,
        jobExecutions: [],
        executionTableStartIndex: 0,
        executionTableItemsPerPage: 10,
        totalJobExecutions: 0,
        executionsLoading: false,
        jobRunStatus: JobStatus.NONE,
        modeler: null,
        editMode: false,
        emsSpaces: [],
        hasError: false,
        boardIsEmpty: true,
        unlockInterval: null,
        askInterval: null,
        showAskToStay: false,
        //true while the rule updates coming from outside the portal are applied on the graph,
        //it lives in the store because entering edit mode remounts the graph editor
        applyingUpdates: false,
        textRules: {
            connections: [],
            initial: [],
            states: [],
            roles: [],
            checks: [],
        },
        expressionCurrentEditId: null,
    },
    mutations: {
        setExecutionTableStartIndex: (state, value) => {
            state.executionTableStartIndex = value;
        },
        updateJson: async (state) => {
            const result = await state.modeler.saveXML({format: true});
            const xmlData = result.xml;
            state.jsonData = xmlToJson(xmlData);
        },
        setJobRunStatus: (state, value) => {
            state.jobRunStatus = value;
        },
        setModeler: (state, value) => {
            state.modeler = value;
        },
        setEditMode: (state, value) => (state.editMode = value),
        setHasError: (state, value) => (state.hasError = value),
        setBoardIsEmpty: (state, value) => (state.boardIsEmpty = value),
        setShowAskToStay: (state, value) => (state.showAskToStay = value),
        setApplyingUpdates: (state, value) => (state.applyingUpdates = value),
        setTextRules: (state, {connections, initial, states, roles, checks}) => {
            state.textRules.connections = connections;
            state.textRules.initial = initial;
            state.textRules.states = states;
            state.textRules.roles = roles;
            state.textRules.checks = checks;
        },
        setExpressionCurrentEditId: (state, value) => {
            state.expressionCurrentEditId = value;
        },
    },
    actions: {
        updateGraphFields: async (
            {rootGetters, rootState, dispatch},
            {data, graph = rootGetters["repoStore/getGraph"]}
        ) => {
            if (!graph) {
                return;
            }
            const graphData = await trackPending(OP.UPDATE_GRAPH, () => graphsApi.updateGraph(graph.id, data));
           
            await dispatch("repoStore/updateStoredGraph", graphData, {root: true});
            dispatch("setUnlockInterval");
        },
        getJobStatus: async ({state, rootState}) => {
            const list = await graphsApi.getLastJobExecution(rootState.repoStore.selectedGraphId);
            if (list.length === 0) {
                return {status: JobStatus.NONE, start_date: null};
            }
            state.jobExecutions[0] = list[0];
            return list[0];
        },
        loadJobExecutions: async ({state, rootState}) => {
            state.executionsLoading = true;
            state.totalJobExecutions = (
                await graphsApi.countJobExecutions(rootState.repoStore.selectedGraphId)
            ).count;
            state.jobExecutions = await graphsApi.listJobExecutions(
                rootState.repoStore.selectedGraphId,
                {count: state.executionTableItemsPerPage, from: state.executionTableStartIndex},
            );
            state.executionsLoading = false;
        },
        runJob: async ({rootState, dispatch}) => {
            const res = await trackPending(OP.RUN_JOB, () => graphsApi.runJob(rootState.repoStore.selectedGraphId));
            dispatch("loadJobExecutions");
            return res;
        },
        updateActivityTableNames: async ({
                                             state,
                                             rootGetters,
                                             commit,
                                             getters,
                                             dispatch,
                                         }) => {
            const activities = getActivityList(state.modeler.get('canvas'));
            activities.forEach((activity) => {
                const tableName = rootGetters["repoStore/getActivityTable"](
                    getActivityName(activity)
                );
                state.modeler.get("modeling").updateProperties(activity, {
                    tableName,
                });
            });
            await commit("updateJson");
            const jsonData = getters.jsonData;
            await dispatch("updateGraphFields", {
                data: {definition: jsonData},
            });
        },
        checkLockedBy: async ({rootState, dispatch}) => {
            const lockedBy = await trackPending(OP.GET_LOCK, () => graphsApi.getGraphLock(rootState.repoStore.selectedGraphId));
            await dispatch("repoStore/updateStoredGraph", lockedBy, {root: true});
        },
        toggleEditMode: async ({state, commit, rootState, dispatch}) => {
            const currentEdit = state.editMode;
            const newEdit = !currentEdit;

            try {
                if (newEdit) {
                    // update graph to get latest version (if someone else edited the graph) 
                    const graphData = await dispatch('repoStore/getGraph', {
                        graphId: rootState.repoStore.selectedGraphId
                    }, {root: true})
                    await dispatch('repoStore/updateStoredGraph', graphData, {root: true})

                    //entering edit mode
                    await dispatch("lockGraph");
                    dispatch("repoStore/loadActivities", null, { root: true });
                    commit("repoStore/setSelectedVersion", "draft", {root: true});
                    commit("setEditMode", true);
                    return;
                }

                //exiting edit mode
                commit("setEditMode", false);
                await dispatch("unlockGraph");
            } catch (error) {
                if (!newEdit) {
                    await dispatch("checkLockedBy");
                    const lockedBy = rootState.repoStore.currentGraph?.locked_by;
                    const currentUser = rootState.userStore.user?.email;
                    if (lockedBy && lockedBy === currentUser) {
                        commit("setEditMode", true);
                    }
                }
                throw error;
            } finally {
                commit("setShowAskToStay", false);
            }
        },
        setUnlockInterval: ({state, dispatch, commit}) => {
            commit("setShowAskToStay", false);
            if (state.unlockInterval) {
                clearInterval(state.unlockInterval);
                clearInterval(state.askInterval);
            }
            state.askInterval = setInterval(() => {
                if (state.editMode) {
                    commit("setShowAskToStay", true);
                }
            }, (LOCK_MINUTES-1) * 60 * 1000);
            state.unlockInterval = setInterval(async () => {
                if (state.editMode) {
                    await dispatch("toggleEditMode");
                    commit("setShowAskToStay", false);
                }
            }, LOCK_MINUTES * 60 * 1000);
        },
        lockGraph: async ({state, rootState, dispatch}) => {
            await trackPending(OP.LOCK_GRAPH, () => graphsApi.lockGraph(rootState.repoStore.selectedGraphId));
            dispatch("setUnlockInterval");
        },
        unlockGraph: async ({rootState}) => {
            await trackPending(OP.UNLOCK_GRAPH, () => graphsApi.unlockGraph(rootState.repoStore.selectedGraphId));
        },
        loadEmsSpaces: async ({state}, force = false) => {
            if (state.emsSpaces.length === 0 || force) {
                state.emsSpaces = await trackPending(OP.GET_STUDIO_SPACES, () => studioApi.listEmsSpaces());
            }
            return state.emsSpaces
        },
    },
    getters: {
        getModeler: (state) => state.modeler,
        getExecutionTableStartIndex: (state) => state.executionTableStartIndex,
        getJobExecutions: (state) => state.jobExecutions,
        getExecutionsLoading: (state) => state.executionsLoading,
        getTotalExecutions: (state) => state.totalJobExecutions,
        jsonData: (state) => state.jsonData,
        jobRunStatus: (state) => state.jobRunStatus,
        jobRunning: (state) =>
            [JobStatus.PENDING, JobStatus.RUNNING, JobStatus.SCHEDULED].includes(
                state.jobRunStatus
            ),
        getEditMode: (state) => state.editMode,
        getApplyingUpdates: (state) => state.applyingUpdates,
        getHasError: (state) => state.hasError,
        getEmsSpaces: (state) => state.emsSpaces,
        invalidGraph: (state) => state.hasError || state.boardIsEmpty,
        editable: (state, getters, rootState, rootGetters) =>
            rootGetters["repoStore/getSelectedVersion"] === "draft" &&
            rootGetters["repoStore/canEditGraph"] &&
            getters.getEditMode,
        isLockedByAnotherUser:
            (state, getters, rootState, rootGetters) =>
                (graph = rootGetters["repoStore/getGraph"]) => {
                    // check if graph is locked by another user
                    return (
                        graph &&
                        graph.locked_by &&
                        graph.locked_by !== rootState.userStore.user.email
                    );
                },
        lockTimedOut: (state, getters, rootState, rootGetters) => {
            // get graph from repoStore
            const graph = rootGetters["repoStore/getGraph"];
            // check if getGraph.locked_date is older than 5 minutes
            if (graph && graph.locked_date) {
                const lockedDate = new Date(graph.locked_date);
                const currentDate = new Date();

                const diff = currentDate - lockedDate;
                return diff > 60 * LOCK_MINUTES * 1000;
            }
            return true;
        },
        getShowAskToStay: (state) => state.showAskToStay,
        graphNotes: (state, getters, rootState, rootGetters) => {
            const graph = rootGetters["repoStore/getGraph"];
            return graph ? graph.notes : "";
        },
        getTextRules: (state) => {
            return state.textRules;
        },
        getExpressionCurrentEditId: (state) => {
            return state.expressionCurrentEditId;
        }
    },
};
